import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

import { formatMegabytes, pluralize } from "@/lib/utils"

interface BridgeMessage {
  event: string
  data: unknown
}

interface DownloadedContainer {
  id: string
  title: string
  path: string
  imageUrl?: string
  imageData?: string
  downloadedCount: number
  byteSize: number
}

interface LibraryData {
  containers?: DownloadedContainer[]
}

interface Cover {
  source: string
  fallback?: string
}

const VERSION = 1

const MOSAIC_LAYOUT: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-2 grid-rows-2",
}

const presence = (value?: string) => (value === "" ? undefined : value)

export default class extends BridgeComponent {
  static component = "library"
  static targets = ["grid", "template", "card"]

  declare readonly gridTarget: HTMLElement
  declare readonly templateTarget: HTMLTemplateElement
  declare readonly cardTargets: HTMLElement[]

  connect() {
    super.connect()
    this.send("connect", { version: VERSION }, (message: BridgeMessage) =>
      this.receive(message),
    )
  }

  receive(message: BridgeMessage) {
    if (message.event !== "library") return

    const { containers } = (message.data ?? {}) as LibraryData
    this.#render(Array.isArray(containers) ? containers : [])
  }

  showOffline() {
    this.send("showOffline", { version: VERSION })
  }

  #render(containers: DownloadedContainer[]) {
    this.cardTargets.forEach((card) => card.remove())
    if (containers.length === 0) return

    const card = (
      this.templateTarget.content.cloneNode(true) as DocumentFragment
    ).firstElementChild!

    this.#fillMosaic(card, this.#covers(containers))
    card.querySelector("[data-card='meta']")!.textContent =
      this.#meta(containers)

    this.gridTarget.prepend(card)
  }

  #covers(containers: DownloadedContainer[]): Cover[] {
    const covers = new Map<string, Cover>()
    containers.forEach((container) => {
      const source = presence(container.imageUrl) ?? container.imageData
      if (!source || covers.has(source)) return

      covers.set(source, { source, fallback: container.imageData })
    })

    return [...covers.values()].slice(0, 4)
  }

  #fillMosaic(card: Element, covers: Cover[]) {
    const images = card.querySelectorAll<HTMLImageElement>(
      "[data-card='cover']",
    )
    const placeholder = card.querySelector<HTMLElement>(
      "[data-card='placeholder']",
    )!

    if (covers.length === 0) {
      placeholder.classList.replace("hidden", "flex")
      return
    }

    const mosaic = card.querySelector<HTMLElement>("[data-card='mosaic']")!
    mosaic.classList.add(...MOSAIC_LAYOUT[covers.length].split(" "))

    let remaining = covers.length
    covers.forEach(({ source, fallback }, index) => {
      const image = images[index]
      image.onerror = () => {
        if (fallback && image.src !== fallback) {
          image.src = fallback
          return
        }

        image.classList.add("hidden")
        remaining -= 1
        if (remaining === 0) placeholder.classList.replace("hidden", "flex")
      }
      image.src = source
      image.classList.remove("hidden")
    })
  }

  #meta(containers: DownloadedContainer[]): string {
    const label = pluralize(containers.length, "container")
    const size = containers.reduce(
      (total, container) => total + (Number(container.byteSize) || 0),
      0,
    )
    if (size <= 0) return label

    return `${label} · ${formatMegabytes(size)}`
  }
}
